import { read_cookie } from 'sfcookies';
import { apiUrl, hostUrl, UseUrlManagerCleanUrl } from '../../../utils/url-manager';
import { NokProcessingFailure } from '../../../../types/entities';

/**
 * Reading and clearing Nok scoring failures.
 *
 * Direct to HQ-API rather than through a react-admin data provider, for the same reason the sales
 * conversation panel is: the providers swallow a non-2xx and hand react-admin an empty result
 * (HQ-06), and on this surface that is the worst possible failure. A rescore that HQ-API refused
 * would render as one that started, and the person who asked for it would go away believing the
 * work was requeued.
 */

export type RescoreResult = {
    status: 'success' | 'error',
    message: string,
    queued: number,
    /** Bulk only: how many failures matched, which may be more than were queued. */
    matched?: number,
};

const jsonHeaders = (): Headers => new Headers({
    'Content-Type': 'application/json',
    'token': read_cookie('auth'),
    'hq': 'hq',
});

/**
 * Failures nobody has cleared yet, newest first.
 *
 * 'resolved' rows are left out: they are runs that failed and then scored, which is the system
 * working rather than something to look at.
 *
 * @param limit how many rows to fetch
 */
export const loadUnresolvedFailures = async (limit: number = 10): Promise<NokProcessingFailure[]> => {
    const url: string = UseUrlManagerCleanUrl({
        url: `${ apiUrl }nok_processing_failures?status[]=open&status[]=retrying`
            + `&sortOrder[createdAt]=desc&itemsPerPage=${ limit }`,
    });
    const response: Response = await fetch(new Request(url, { method: 'GET', headers: jsonHeaders() }));
    if (!response.ok) {
        throw new Error('Could not load the processing failures.');
    }

    const body: any = await response.json();

    return body?.['hydra:member'] ?? body?.member ?? [];
};

/**
 * The failure a single result is carrying, or null when it scored cleanly.
 *
 * Null is the ordinary answer: most results never fail, so the panel on a result screen renders
 * nothing at all rather than an empty box saying so.
 */
export const loadFailureForResult = async (
    mini: string,
    resultId: number,
): Promise<NokProcessingFailure|null> => {
    const url: string = UseUrlManagerCleanUrl({
        url: `${ apiUrl }nok_processing_failures?mini=${ mini }&resultId=${ resultId }`
            + `&status[]=open&status[]=retrying&sortOrder[createdAt]=desc&itemsPerPage=1`,
    });
    const response: Response = await fetch(new Request(url, { method: 'GET', headers: jsonHeaders() }));
    if (!response.ok) {
        throw new Error('Could not load the processing failure.');
    }

    const body: any = await response.json();
    const members: NokProcessingFailure[] = body?.['hydra:member'] ?? body?.member ?? [];

    return members.length > 0 ? members[0] : null;
};

/**
 * Reads the message out of an HQ-API answer.
 *
 * Both rescore routes answer `{status, message, queued}` either way, so the message is worth
 * showing rather than replacing with something generic.
 */
const readResult = async (response: Response, fallback: string): Promise<RescoreResult> => {
    try {
        const body: any = await response.json();
        if (typeof body?.status === 'string') {
            return {
                status: body.status === 'success' ? 'success' : 'error',
                message: typeof body.message === 'string' ? body.message : fallback,
                queued: typeof body.queued === 'number' ? body.queued : 0,
                matched: typeof body.matched === 'number' ? body.matched : undefined,
            };
        }
    } catch {
        // Not JSON. The status is all there is to go on.
    }

    return { status: 'error', message: fallback, queued: 0 };
};

/**
 * @param path the action route under /hq
 * @param payload the body
 * @param fallback what to say when HQ-API gave no message of its own
 */
const post = async (path: string, payload: object, fallback: string): Promise<RescoreResult> => {
    const url: string = UseUrlManagerCleanUrl({ url: `${ hostUrl }hq/${ path }` });
    let response: Response;
    try {
        response = await fetch(new Request(url, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify(payload),
        }));
    } catch {
        // The request never landed, which is different from one HQ-API refused and needs saying
        // so: nothing was queued and retrying is worth a go.
        return { status: 'error', message: 'Could not reach HQ-API.', queued: 0 };
    }

    return readResult(response, fallback);
};

/**
 * Score one failed result again.
 *
 * Takes the failure's own id, or the mini and result together for the button on a result screen,
 * where the failure row's id is not on the page.
 */
export const rescoreOne = async (
    target: { failureId: number } | { mini: string, resultId: number },
): Promise<RescoreResult> =>
    post('nok/processing/rescore', target, 'The rescore could not be started.');

/**
 * Score a set of failed results again.
 *
 * The date range is the shape a rescore after a bug fix takes: the fix has a date, so the
 * failures it explains do too.
 */
export const rescoreMany = async (
    target: { failureIds: number[] } | { from: string, to: string, mini?: string },
): Promise<RescoreResult> =>
    post('nok/processing/rescore/bulk', target, 'The rescore could not be started.');

/**
 * Send one stored reply to the Operator for its robot-or-human check again.
 *
 * For a reply whose verdict never came back. HQ-API refuses a reply that is not checked (sales
 * mail, a cancelled run, an enquiry it cannot find) and says why, so the message is shown as is.
 */
export const checkRobotOrHumanAgain = async (emailId: number): Promise<RescoreResult> =>
    post(`nok/email/${ emailId }/robot-or-human`, {}, 'The check could not be started.');
